// Client Portal — shared podcast UI + constants used by the Podcast and
// Video portals (and the podcast stats view in App.js).
import { useState } from "react";
import { Loader2, Play, X } from "lucide-react";
import { AUTH_URL, getToken } from "../lib/api";
import { showToast } from "../lib/toast";

export const PODCAST_STYLES_FALLBACK = [{ value: "", label: "— None —" }];

export const PODCAST_BUSY_STATUSES = [
  "queued",
  "script_generating",
  "audio_queued",
  "audio_generating",
];

export const PODCAST_EDITABLE_STATUSES = ["draft", "script_ready", "failed"];

export const PODCAST_STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-600",
  queued: "bg-yellow-100 text-yellow-700",
  script_generating: "bg-yellow-100 text-yellow-700",
  script_ready: "bg-amber-100 text-amber-700",
  audio_queued: "bg-yellow-100 text-yellow-700",
  audio_generating: "bg-yellow-100 text-yellow-700",
  ready: "bg-blue-100 text-blue-700",
  published: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  archived: "bg-slate-100 text-slate-500",
};

export const PODCAST_STATUS_LABELS = {
  draft: "Draft",
  queued: "Queued",
  script_generating: "Writing script…",
  script_ready: "Awaiting approval",
  audio_queued: "Audio queued",
  audio_generating: "Generating audio…",
  ready: "Ready",
  published: "Published",
  failed: "Failed",
  archived: "Archived",
};

export const PODCAST_PROVIDERS = [
  { id: "azure", label: "Azure" },
  { id: "elevenlabs", label: "ElevenLabs" },
  { id: "piper", label: "Piper (local)" },
];

// authFetch returns parsed JSON; on non-2xx it returns { error: rawText }.
export const podcastErr = (data, fallback) => {
  if (!data || !data.error) return null;
  try {
    const parsed = JSON.parse(data.error);
    // Prefer the human-readable message (e.g. quota_exceeded responses)
    return parsed.message || parsed.error || fallback;
  } catch {
    return fallback;
  }
};

// Authenticated audio fetch -> blob URL (audio endpoints need the JWT header,
// so a plain <audio src> won't work).
export const fetchPodcastAudioUrl = async (endpoint) => {
  const res = await fetch(`${AUTH_URL}${endpoint}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error(`Audio fetch failed (${res.status})`);
  return URL.createObjectURL(await res.blob());
};

export const podcastInputCls =
  "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500";

export const PodcastField = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 mb-1">
      {label}
      {required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

export const PodcastModal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div
      className={`bg-white rounded-xl shadow-xl w-full ${
        wide ? "max-w-2xl" : "max-w-lg"
      } max-h-[90vh] overflow-y-auto`}
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white rounded-t-xl">
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

export const PodcastAudioPlayer = ({ endpoint, label }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setUrl(await fetchPodcastAudioUrl(endpoint));
    } catch {
      showToast("Could not load audio. Please try again.");
    }
    setLoading(false);
  };

  if (url) {
    return <audio controls autoPlay src={url} className="w-full h-9" />;
  }
  return (
    <button
      onClick={load}
      disabled={loading}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 text-white text-xs font-medium rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50"
    >
      {loading ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Play className="w-3.5 h-3.5" />
      )}
      {label || "Listen"}
    </button>
  );
};
